import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SwUpdate } from '@angular/service-worker';
import { UpdateDialogComponent } from '@library/components/update-dialog/version-update-dialog.component';
import pkg from '../../../package.json';

@Injectable({
  providedIn: 'root',
})
export class AppVersionService {
  private readonly VERSION_KEY = 'app_version';
  private readonly UPDATE_PARAM = 'app_updated';

  constructor(
    private swUpdate: SwUpdate,
    private dialog: MatDialog
  ) {}

  public getPkgVersion(): string | null {
    return pkg.version;
  }

  public getLocalStorageVersion(): string | null {
    return localStorage.getItem(this.VERSION_KEY);
  }

  public checkVersion(): void {
    const currentVersion = pkg.version;
    const storedVersion = localStorage.getItem(this.VERSION_KEY);

    if (!storedVersion) {
      localStorage.setItem(this.VERSION_KEY, currentVersion);
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has(this.UPDATE_PARAM)) {
      const newUrl =
        window.location.pathname + window.location.search.replace(new RegExp(`[?&]${this.UPDATE_PARAM}=true`), '');
      window.history.replaceState({}, document.title, newUrl);
      this.showUpdateDialog();
    } else if (storedVersion !== currentVersion) {
      this.performHardReset()
        .then(() => {})
        .catch((error) => {
          console.error('Error during hard reset:', error);
        })
        .finally(() => {
          localStorage.setItem(this.VERSION_KEY, currentVersion);
          window.location.href = window.location.origin + `?${this.UPDATE_PARAM}=true`;
        });
    }
  }

  private showUpdateDialog(): void {
    this.dialog.open(UpdateDialogComponent, {
      width: '400px',
      disableClose: true,
    });
  }

  private async performHardReset(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const isTabletMode = localStorage.getItem('tabletModeEnabled') === 'true';
        const enterpriseId = localStorage.getItem('enterpriseId');
        localStorage.clear();
        localStorage.setItem(this.VERSION_KEY, pkg.version);
        if (isTabletMode && enterpriseId) {
          localStorage.setItem('tabletModeEnabled', 'true');
          localStorage.setItem('enterpriseId', enterpriseId);
        }

        sessionStorage.clear();

        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
        }

        if (navigator.serviceWorker) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const registration of registrations) {
            await registration.unregister();
          }
        }

        if (this.swUpdate.isEnabled) {
          try {
            const updated = await this.swUpdate.activateUpdate();
          } catch (err) {
            console.error('Service worker update error:', err);
          }
        }

        resolve();
      } catch (error) {
        console.error('Error during app reset:', error);
        reject(error);
      }
    });
  }
}
